import { getSelectorAttribute, usageProductVariantHeaderEditSelectors } from '@weft/app-selectors';
import type { UsageProductVariantInput } from '@weft/types';
import { isMultiple, isNotNullOrUndefined, removeInIndex } from '@weft/utils';
import { TrashIcon } from 'apps/app/src/components/icons/TrashIcon';
import { vyFormRegister } from 'apps/app/src/utils/react-hook-forms-utils';
import React, { useCallback } from 'react';
import { useFormContext, get, Controller } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { Input } from '../../../../../../components/design/Input';
import { useFormErrorNotificationContext } from '../../../../../../contexts/form-error-notification-context/form-error-notification-context';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { CreatePlanTab } from '../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { CREATE_PLAN_TABS } from '../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { ProductSourceSelect } from './ProductSourceSelect/ProductSourceSelect';

export type UsageProductVariantHeaderEditProps = {
  productIndex: number,
  productVariant: UsageProductVariantInput,
  productVariantIndex: number,
};

export const UsageProductVariantHeaderEdit: React.FC<UsageProductVariantHeaderEditProps> = ({ productIndex, productVariant, productVariantIndex }) => {
  const { registerErrorField } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();
  const { register, control, getFieldState, setValue, formState: { errors }, getValues } = useFormContext<PlanInputForm>();

  const nameRegister = register(`usageProducts.${productIndex}.variants.${productVariantIndex}.name`);
  const descriptionRegister = vyFormRegister(register, `usageProducts.${productIndex}.variants.${productVariantIndex}.description`);
  const productVariants = getValues(`usageProducts.${productIndex}.variants`);

  const onDelete = useCallback(() => {
    const updatedProductVariants = removeInIndex(productVariants, productVariantIndex);
    setValue(`usageProducts.${productIndex}.variants`, updatedProductVariants);
  }, [productIndex, productVariantIndex, productVariants]);

  const canDelete = isMultiple(productVariants);
  registerErrorField(`usageProducts.${productIndex}.variants.${productVariantIndex}`, CREATE_PLAN_TABS.tierAndVolumePricing);
  registerErrorField(nameRegister.name, CREATE_PLAN_TABS.tierAndVolumePricing);
  registerErrorField(descriptionRegister.name, CREATE_PLAN_TABS.tierAndVolumePricing);

  return (
    <div className='mb-5 flex flex-wrap justify-between gap-4'>
      <div className="flex min-w-[190px] flex-1 flex-col pt-3">
        <Controller
          control={control}
          name={`usageProducts.${productIndex}.variants.${productVariantIndex}.meterId`}
          render={({ field }) => (
            <ProductSourceSelect
              error={isNotNullOrUndefined(get(errors, `usageProducts.${productIndex}.variants.${productVariantIndex}.meterId`))}
              helperText={getFieldState(`usageProducts.${productIndex}.variants.${productVariantIndex}.meterId`).error?.message}
              defaultValue={productVariant.meterId}
              value={field.value}
              onChange={(event, newValue) => {
                event.preventDefault();
                field.onChange(newValue);
              }}
            />
          )}
        />
      </div>
      <div className="flex min-w-[190px] flex-1">
        <Input
          {...getSelectorAttribute(usageProductVariantHeaderEditSelectors.nameInput)}
          label='Product name'
          placeholder='Text here'
          {...nameRegister}
          debounce={false}
          defaultValue={getValues(nameRegister.name)}
          errorMessage={get(errors, nameRegister.name)?.message}
          className={twMerge('max-w-[100%] text-base placeholder:font-normal', getFieldState(`usageProducts.${productIndex}.variants.${productVariantIndex}.name`).invalid ? 'border border-input-error' : '')}
        />
      </div>
      {canDelete && (
        <div className='flex justify-end'>
          <TrashIcon
            className='fill-icon mb-3 self-end'
            onClick={onDelete}
          />
        </div>
      )}
    </div>
  );
};